import React from 'react'
import { useTranslation } from 'react-i18next'
import { Activity, ShieldAlert, Server } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import JimiHealthCard from './JimiHealthCard'

// SystemMonitoring — admin-only operator's panel for the technical
// plumbing that keeps the platform alive. Today it shows the JIMI
// ingest pipeline (TurboHive MQTT → jimi-receiver → jimi-bridge →
// orchestration_db). New back-end integrations (future Jointech /
// MaxVision health, queue depths, sync workers, …) plug in here as
// additional cards so end-user UIs stay clean.
//
// Gated to ADMIN role: this surface intentionally exposes raw cycle
// counters, watermark lag, MQTT topic counts and other vendor-side
// numbers that are noise for fleet operators and only useful for the
// person operating the platform itself.

export interface SystemMonitoringProps {
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

const SystemMonitoring: React.FC<SystemMonitoringProps> = () => {
  const { t } = useTranslation()
  const { user } = useAuth()

  if (user?.role !== 'ADMIN') {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <ShieldAlert className="w-12 h-12 text-gray-300 mb-3" />
        <h2 className="text-lg font-semibold text-gray-700">
          {t('system_monitoring_admin_only_title', { defaultValue: 'Administrator access required' })}
        </h2>
        <p className="mt-1 text-sm text-gray-500 max-w-md">
          {t('system_monitoring_admin_only_msg', {
            defaultValue:
              'This panel exposes platform-side health metrics. Ask an administrator if you need access.',
          })}
        </p>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-rose-50 border border-rose-100">
          <Server className="w-5 h-5 text-rose-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {t('system_monitoring_title', { defaultValue: 'System monitoring' })}
          </h1>
          <p className="text-sm text-gray-500">
            {t('system_monitoring_subtitle', {
              defaultValue: 'Platform-side health for vendor integrations and ingest pipelines',
            })}
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-rose-600" />
            {t('system_monitoring_jimi_section', { defaultValue: 'JIMI ingest pipeline' })}
          </h2>
          <JimiHealthCard />
        </section>
      </div>
    </div>
  )
}

export default SystemMonitoring
